import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, Minus, Plus, AlertCircle, Package } from "lucide-react";
import { formatCurrency, cn, resolveImageUrl } from "@/lib/utils";
import { useAuthStore } from "@/store/useStore";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductCustomizationModal({
  open,
  onClose,
  product,
  onAddToCart
}) {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [customizationConfig, setCustomizationConfig] = useState(null);
  const [quantity, setQuantity] = useState(1);
  
  // Selection state
  const [prepOptionSelected, setPrepOptionSelected] = useState("");
  const [prepInstructions, setPrepInstructions] = useState("");
  const [optionSelections, setOptionSelections] = useState({}); // { groupId: [optionIds] }
  const [mealSlotSelections, setMealSlotSelections] = useState({}); // { slotId: productId }
  
  // Validation
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (open && product) {
      fetchCustomizationConfig();
      resetSelections();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product]);

  const resetSelections = () => {
    setQuantity(1);
    setPrepOptionSelected("");
    setPrepInstructions("");
    setOptionSelections({});
    setMealSlotSelections({});
    setErrors([]);
  };

  const fetchCustomizationConfig = async () => {
    if (!product?.id) return;
    
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/products/${product.id}/option-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCustomizationConfig(data);
        
        // Set defaults
        const defaultSelections = {};
        data.option_groups?.forEach(group => {
          const defaultOptions = group.options?.filter(o => o.is_default) || [];
          if (defaultOptions.length > 0) {
            defaultSelections[group.id] = defaultOptions.map(o => o.id);
          }
        });
        setOptionSelections(defaultSelections);
        
        // Set default prep option if any
        if (data.prep_options?.length > 0) {
          const defaultPrep = data.prep_options[0];
          if (defaultPrep) setPrepOptionSelected(defaultPrep);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (groupId, optionId, selectionType) => {
    setOptionSelections(prev => {
      const current = prev[groupId] || [];
      
      if (selectionType === "single") {
        // Radio button - only one selection
        return { ...prev, [groupId]: [optionId] };
      } else {
        // Checkbox - multiple selections
        if (current.includes(optionId)) {
          return { ...prev, [groupId]: current.filter(id => id !== optionId) };
        } else {
          return { ...prev, [groupId]: [...current, optionId] };
        }
      }
    });
  };

  const isOptionSelected = (groupId, optionId) => {
    return (optionSelections[groupId] || []).includes(optionId);
  };

  const calculateTotal = () => {
    // For meal slots, use the fixed meal price
    if (customizationConfig?.meal_type === "meal_slot") {
      return (customizationConfig?.meal_price || product?.price || 0) * quantity;
    }
    
    let total = product?.price || 0;
    
    // Add option prices
    customizationConfig?.option_groups?.forEach(group => {
      const selectedIds = optionSelections[group.id] || [];
      group.options?.forEach(option => {
        if (selectedIds.includes(option.id)) {
          total += option.price || 0;
        }
      });
    });
    
    // Add inline option prices
    customizationConfig?.inline_options?.forEach(group => {
      const selectedIds = optionSelections[group.id] || [];
      group.options?.forEach(option => {
        if (selectedIds.includes(option.id)) {
          total += option.price || 0;
        }
      });
    });
    
    return total * quantity;
  };

  const validate = () => {
    const newErrors = [];
    
    // Check required prep options
    if (customizationConfig?.prep_option_required && 
        customizationConfig?.prep_instruction_type !== "text" &&
        !prepOptionSelected) {
      newErrors.push("Please select a preparation option");
    }
    
    // Check required option groups
    customizationConfig?.option_groups?.forEach(group => {
      const selectedCount = (optionSelections[group.id] || []).length;
      
      if (group.min_selections > 0 && selectedCount < group.min_selections) {
        newErrors.push(`Please select at least ${group.min_selections} option(s) for "${group.name}"`);
      }
      
      if (group.selection_type === "multiple" && selectedCount > group.max_selections) {
        newErrors.push(`You can only select up to ${group.max_selections} option(s) for "${group.name}"`);
      }
    });
    
    // Check inline options
    customizationConfig?.inline_options?.forEach(group => {
      const selectedCount = (optionSelections[group.id] || []).length;
      
      if (group.min_selections > 0 && selectedCount < group.min_selections) {
        newErrors.push(`Please select at least ${group.min_selections} option(s) for "${group.name}"`);
      }
    });
    
    // Check required meal slots
    customizationConfig?.meal_slots?.forEach(slot => {
      if (slot.is_required && !mealSlotSelections[slot.id]) {
        newErrors.push(`Please select a ${slot.name}`);
      }
    });
    
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleAddToCart = () => {
    if (!validate()) return;
    
    // Build selected options list
    const selectedOptions = [];
    
    customizationConfig?.option_groups?.forEach(group => {
      const selectedIds = optionSelections[group.id] || [];
      group.options?.forEach(option => {
        if (selectedIds.includes(option.id)) {
          selectedOptions.push({
            group_id: group.id,
            group_name: group.name,
            option_id: option.id,
            option_name: option.name,
            price: option.price || 0,
            linked_product_id: option.linked_product_id
          });
        }
      });
    });
    
    customizationConfig?.inline_options?.forEach(group => {
      const selectedIds = optionSelections[group.id] || [];
      group.options?.forEach(option => {
        if (selectedIds.includes(option.id)) {
          selectedOptions.push({
            group_id: group.id,
            group_name: group.name,
            option_id: option.id,
            option_name: option.name,
            price: option.price || 0,
            linked_product_id: option.linked_product_id
          });
        }
      });
    });
    
    // Calculate options total
    const optionsTotal = selectedOptions.reduce((sum, opt) => sum + (opt.price || 0), 0);
    
    // Build meal selections if this is a slot-based meal
    const mealSelections = [];
    if (customizationConfig?.meal_type === "meal_slot" && customizationConfig?.meal_slots) {
      customizationConfig.meal_slots.forEach(slot => {
        const selectedProductId = mealSlotSelections[slot.id];
        if (selectedProductId) {
          const selectedProduct = slot.products?.find(p => p.id === selectedProductId);
          if (selectedProduct) {
            mealSelections.push({
              slot_id: slot.id,
              slot_name: slot.name,
              product_id: selectedProduct.id,
              product_name: selectedProduct.name
            });
          }
        }
      });
    }
    
    // Determine the actual price to use
    const isMeal = customizationConfig?.meal_type === "meal_slot";
    const finalPrice = isMeal 
      ? (customizationConfig?.meal_price || product.price)
      : (product.price + optionsTotal);
    
    // Build customized product
    const customizedProduct = {
      ...product,
      prep_instructions: prepInstructions,
      prep_options_selected: prepOptionSelected ? [prepOptionSelected] : [],
      selected_options: selectedOptions,
      options_total: optionsTotal,
      customized_price: finalPrice,
      meal_selections: mealSelections,
      is_meal: isMeal,
      // Unique key for cart (same product with different options = different cart item)
      customization_key: JSON.stringify({
        prep: prepOptionSelected,
        options: optionSelections,
        meal_slots: mealSlotSelections
      })
    };
    
    onAddToCart(customizedProduct, quantity);
    onClose();
  };

  const hasCustomizations = () => {
    if (!customizationConfig) return false;
    return (
      customizationConfig.prep_instruction_enabled ||
      (customizationConfig.option_groups?.length > 0) ||
      (customizationConfig.inline_options?.length > 0) ||
      (customizationConfig.meal_type === "meal_slot" && customizationConfig.meal_slots?.length > 0)
    );
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If no customizations, this modal shouldn't be shown
  // But handle it gracefully
  if (!hasCustomizations()) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="p-4 pb-2 shrink-0 border-b">
          <DialogTitle className="flex items-center justify-between">
            <span className="truncate pr-4">{product?.name}</span>
            <span className="text-primary font-mono shrink-0">
              {formatCurrency(
                customizationConfig?.meal_type === "meal_slot" 
                  ? (customizationConfig?.meal_price || product?.price)
                  : product?.price
              )}
            </span>
          </DialogTitle>
          {customizationConfig?.meal_type === "meal_slot" && (
            <p className="text-xs text-muted-foreground">Fixed meal price - choose your items below</p>
          )}
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto px-4 py-2 min-h-0">
          <div className="space-y-6 pb-4">
            {/* Meal Slot Selections */}
            {customizationConfig?.meal_type === "meal_slot" && customizationConfig?.meal_slots?.map((slot) => (
              <div key={slot.id} className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {slot.name}
                  </Label>
                  {slot.is_required && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Required</Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  {slot.products?.map((slotProduct) => {
                    const isSelected = mealSlotSelections[slot.id] === slotProduct.id;
                    return (
                      <div
                        key={slotProduct.id}
                        className={cn(
                          "flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors",
                          isSelected ? "bg-primary/10 border-primary" : "hover:bg-muted"
                        )}
                        onClick={() => setMealSlotSelections(prev => ({ ...prev, [slot.id]: slotProduct.id }))}
                      >
                        <div className="w-10 h-10 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                          {slotProduct.image_url ? (
                            <img 
                              src={resolveImageUrl(slotProduct.image_url)} 
                              alt={slotProduct.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Package className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{slotProduct.name}</p>
                        </div>
                        {isSelected && (
                          <Check className="h-4 w-4 text-primary shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {(!slot.products || slot.products.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                    No products available for this slot
                  </p>
                )}
              </div>
            ))}
            
            {/* Separator between meal slots and other options */}
            {customizationConfig?.meal_type === "meal_slot" && 
             customizationConfig?.meal_slots?.length > 0 &&
             (customizationConfig?.prep_instruction_enabled || customizationConfig?.option_groups?.length > 0) && (
              <Separator />
            )}
            
            {/* Preparation Instructions */}
            {customizationConfig?.prep_instruction_enabled && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Preparation
                  </Label>
                  {customizationConfig.prep_option_required && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Required</Badge>
                  )}
                </div>
                
                {/* Prep Options */}
                {(customizationConfig.prep_instruction_type === "options" || 
                  customizationConfig.prep_instruction_type === "both") &&
                  customizationConfig.prep_options?.length > 0 && (
                  <RadioGroup value={prepOptionSelected} onValueChange={setPrepOptionSelected}>
                    <div className="grid grid-cols-2 gap-2">
                      {customizationConfig.prep_options.map((option) => (
                        <div
                          key={option}
                          className={cn(
                            "flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors",
                            prepOptionSelected === option
                              ? "bg-primary/10 border-primary"
                              : "hover:bg-muted"
                          )}
                          onClick={() => setPrepOptionSelected(option)}
                        >
                          <RadioGroupItem value={option} id={`prep-${option}`} />
                          <Label htmlFor={`prep-${option}`} className="cursor-pointer flex-1">
                            {option}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                )}
                
                {/* Free text instructions */}
                {(customizationConfig.prep_instruction_type === "text" || 
                  customizationConfig.prep_instruction_type === "both") && (
                  <Textarea
                    value={prepInstructions}
                    onChange={(e) => setPrepInstructions(e.target.value)}
                    placeholder="Special instructions (e.g., no salt, extra sauce)"
                    rows={2}
                    className="resize-none"
                  />
                )}
              </div>
            )}
            
            {/* Option Groups */}
            {customizationConfig?.option_groups?.map((group) => (
              <div key={group.id} className="space-y-3">
                <Separator />
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.name}
                    {group.selection_type === "multiple" && group.max_selections > 1 && (
                      <span className="ml-2 font-normal normal-case">
                        (Select up to {group.max_selections})
                      </span>
                    )}
                  </Label>
                  {group.min_selections > 0 && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Required</Badge>
                  )}
                </div>
                
                {group.description && (
                  <p className="text-sm text-muted-foreground">{group.description}</p>
                )}
                
                <div className="space-y-2">
                  {group.options?.filter(o => o.is_available).map((option) => {
                    const isSelected = isOptionSelected(group.id, option.id);
                    
                    return (
                      <div
                        key={option.id}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                          isSelected ? "bg-primary/10 border-primary" : "hover:bg-muted"
                        )}
                        onClick={() => handleOptionSelect(group.id, option.id, group.selection_type)}
                      >
                        <div className="flex items-center gap-3">
                          {group.selection_type === "single" ? (
                            <div className={cn(
                              "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                              isSelected ? "bg-primary border-primary" : "border-muted-foreground"
                            )}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                          ) : (
                            <Checkbox checked={isSelected} />
                          )}
                          <span className="font-medium">{option.name}</span>
                        </div>
                        {option.price > 0 && (
                          <Badge variant="secondary">+{formatCurrency(option.price)}</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {/* Inline Options */}
            {customizationConfig?.inline_options?.map((group) => (
              <div key={group.id} className="space-y-3">
                <Separator />
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.name}
                  </Label>
                  {group.min_selections > 0 && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Required</Badge>
                  )}
                </div>
                
                <div className="space-y-2">
                  {group.options?.filter(o => o.is_available !== false).map((option) => {
                    const isSelected = isOptionSelected(group.id, option.id);
                    
                    return (
                      <div
                        key={option.id}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                          isSelected ? "bg-primary/10 border-primary" : "hover:bg-muted"
                        )}
                        onClick={() => handleOptionSelect(group.id, option.id, group.selection_type)}
                      >
                        <div className="flex items-center gap-3">
                          {group.selection_type === "single" ? (
                            <div className={cn(
                              "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                              isSelected ? "bg-primary border-primary" : "border-muted-foreground"
                            )}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                          ) : (
                            <Checkbox checked={isSelected} />
                          )}
                          <span className="font-medium">{option.name}</span>
                        </div>
                        {option.price > 0 && (
                          <Badge variant="secondary">+{formatCurrency(option.price)}</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            
            {/* Validation Errors */}
            {errors.length > 0 && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 space-y-1">
                {errors.map((error, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <span>{error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="border-t p-4 space-y-4 shrink-0">
          {/* Quantity */}
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Quantity</Label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center font-semibold">{quantity}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setQuantity(quantity + 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Total & Add Button */}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleAddToCart} className="flex-[2] gap-2">
              <Check className="h-4 w-4" />
              Add to Cart • {formatCurrency(calculateTotal())}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
